import Vue from 'vue';
import io from 'socket.io-client/dist/socket.io.slim.js';
import {consoleLog as _consoleLog, consoleError as _consoleError} from 'xca-app-core/src/utils/console.js';

import {rtcSupported} from './utils.js';
import eventBus from '../event-bus.js';
import {parseError, watchDocumentVisibility} from '../utils.js';


const consoleLog = (...args) => _consoleLog('[WS]', ...args);


const consoleError = (...args) => _consoleError('[WS]', ...args);


const {store, router} = Vue;


const commit = (key, value) => store.commit('share/' + key, value);


export default class ServerConnection {


  constructor() {
    const socket = this.socket = io({transports: ['websocket']});

    const roomConnect = async (...args) => {
      try {
        await this._roomConnect.apply(this, args);
      } catch (error) {
        /* NOOP - Errors already handled by `_roomConnect`. */
      }
    };

    // Add listeners
    socket.on('connect', () => {
      consoleLog('connect');
      this.roomConnection = null; // Shows loading animation.
      roomConnect();
    });

    socket.on('peerConnected', ({peer}) => {
      if (!this.roomConnected) {
        return;
      }
      consoleLog('peerConnected', peer);
      commit('addPeer', peer);
    });

    socket.on('peerDisconnected', ({peerId}) => {
      if (!this.roomConnected) {
        return;
      }
      consoleLog('peerDisconnected', peerId);
      commit('removePeer', peerId);
    });

    socket.on('roomsUpdated', () => {
      consoleLog('roomsUpdated');
      eventBus.$emit('roomsUpdated');
    });

    socket.on('roomDisconnect', ({error}) => {
      if (!this.roomConnected) {
        return;
      }
      consoleLog('roomDisconnect', error);
      socket.emit('roomDisconnect');
      this.roomConnection = parseError(error);
      commit('setPeers', []);
    });

    socket.on('roomDeleted', ({room}) => {
      consoleLog('roomDeleted', room);

      const error = new Error('Room deleted');
      error.statusCode = 410;
      error.room = room;
      this.roomConnection = error;
      this.room = null;

      eventBus.$emit('roomDeleted', room);
    });

    socket.on('roomUpdated', ({room}) => {
      consoleLog('roomUpdated', room);

      addUserProps(room);
      if (this.room && this.room.id === room.id) {
        this.room = room;
      }
      eventBus.$emit('roomUpdated', room);
    });

    socket.on('roomFilesChanged', ({room}) => {
      consoleLog('roomFilesChanged', room);
      eventBus.$emit('roomFilesChanged', room);
    });

    socket.on('notification', ({type, data}) => {
      consoleLog('notification', {type, data});
      store.commit('share/addNotification', {
        type: type.replace(/_/g, '-'),
        store: true,
        data
      });
    });

    socket.on('reconnect', () => {
      consoleLog('reconnect');
      eventBus.$emit('notifyUser', 'Du bist wieder verbunden');
    });

    socket.on('disconnect', (reason) => {
      consoleLog('disconnect', reason);

      const error = new Error(reason);
      error.statusCode = 0;
      this.roomConnection = error;
      commit('setPeers', []);

      if (reason === 'io server disconnect') {
        // The disconnection was initiated by the server, reconnect manually.
        socket.connect();
      }
    });

    socket.on('error', (reason) => {
      switch (reason) {
        case 'authentication failed':
        case 'not authorized':
          Vue.auth.verifyUser();

        // eslint-disable-next-line no-fallthrough
        default:
          consoleError('error', reason);
          break;
      }
    });

    // Check for busy peers before switching room.
    const unwatchBeforeEach = router.beforeEach(async (to, from, next) => {
      if (to.query.room !== from.query.room && this.peersBusy.length) {
        try {
          await new Promise((resolve, reject) => {
            eventBus.$emit('confirmRoomChange', {type: 'peersBusy', resolve, reject});
          });

        } catch (error) {
          return next(from); // Do NOT change route.
        }
      }
      next();
    });

    // Listen for roomId changes.
    const unwatchAfterEach = router.afterEach(() => roomConnect());

    // Listen for visibility changes.
    const unwatchVisibility = watchDocumentVisibility((visible) => {
      if (visible) {
        roomConnect();
      }
    });

    // Listener to force connection.
    const forceRoomConnectListener = () => roomConnect({force: true});
    eventBus.$on('forceRoomConnect', forceRoomConnectListener);

    this._close = () => {
      socket.disconnect();
      unwatchBeforeEach();
      unwatchAfterEach();
      unwatchVisibility();
      eventBus.$off('forceRoomConnect', forceRoomConnectListener);
    };
  }


  get room() {
    return store.getters['share/getRoom'];
  }


  set room(room) {
    return commit('setRoom', room);
  }


  get roomConnection() {
    return store.getters['share/getRoomConnection'];
  }


  set roomConnection(roomConnection) {
    commit('setRoomConnection', roomConnection);
  }


  get roomConnected() {
    return store.getters['share/getRoomConnected'];
  }


  get peersBusy() {
    return Vue.store.getters['share/getPeersBusy'];
  }


  close() {
    this._close();
  }


  _roomConnect(options) {
    return new Promise((resolve, reject) => {
      const routeQuery = router.currentRoute.query;
      options = {
        roomId: routeQuery.room || 'local',
        force: false,
        rtcSupported,
        ...options
      };

      const {roomId} = options;
      if (roomId === this.roomConnection) {
        return resolve(roomId); // Already connected.
      }

      this.roomConnection = null; // Shows loading animation.

      const tourExampleRoom = store.getters['share/getTour'] && roomId === 'example-room';

      this.socket.emit('roomConnect', options, (error, data) => {
        if (error) {
          if (!tourExampleRoom) {
            this.roomConnection = parseError(error);
            this.room = error.data?.room || null;
            commit('setPeers', []);
            commit('setPeerSelf', error.data?.peerSelf || null);
            consoleLog('roomConnect_error', roomId, error);

            return reject(error);

          } else {
            data = {
              room: {
                id: 'example-room',
                name: 'Beispielraum'
              },
              peerSelf: store.getters['share/getPeerSelf']
            };
          }
        }

        this.roomConnection = roomId;
        this.room = data?.room;
        commit('setPeers', data?.peers || []);
        commit('setPeerSelf', data?.peerSelf || null);
        consoleLog('roomConnect', roomId, data);

        resolve(roomId);
      });
    });
  }
}

// XXX: Add `isCurrentUser` && `isOwner` properties
// as in lib/operations/rooms/public-data.js#41
function addUserProps(room) {
  const userId = Vue.store.getters['session/getUser'].id;

  if (room.owner && room.owner.id === userId) {
    room.owner.isCurrentUser = true;
  }

  if (room.participants) {
    for (const participant of room.participants) {
      if (room.owner && room.owner.id === participant.id) {
        participant.isOwner = true;
      }
      if (participant.id === userId) {
        participant.isCurrentUser = true;
      }
    }
  }
}
